import { api } from "@/lib/api";

const MIN_CHUNK = 256 * 1024;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const TOO_LARGE_MSG =
  "The server rejected even a 256 KB piece (HTTP 413). The live site is probably still running an older version — please re-publish the app and try again.";

export const STAGE_LABEL = {
  queued: "Finishing upload…",
  assembling: "Assembling file on the server…",
  inspecting: "Checking video format…",
  converting: "Converting to a browser-playable MP4…",
  storing: "Saving to storage…",
};

// Waits for the server to assemble / convert / store the file. Resolves with the file record.
const waitForProcessing = async (uploadId, onProgress) => {
  for (;;) {
    await sleep(2000);
    const { data } = await api.get(`/uploads/${uploadId}/status`);
    if (data.state === "done") return data.result;
    if (data.state === "error") throw new Error(data.detail || "Processing failed on the server");
    onProgress?.(99, data.stage);
  }
};

// Uploads a file in pieces so no single request exceeds proxy limits (avoids HTTP 413).
// If the proxy still answers 413, the piece size is halved automatically (down to 256 KB) and the same offset is retried.
const uploadInChunks = async (file, kind, onProgress) => {
  const init = await api.post("/uploads/init", { filename: file.name, size: file.size, content_type: file.type || "", kind });
  const { upload_id } = init.data;
  let chunkSize = init.data.chunk_size;
  let offset = 0;
  let attempt = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + chunkSize);
    try {
      await api.post(`/uploads/${upload_id}/chunk`, chunk, {
        params: { offset },
        headers: { "Content-Type": "application/octet-stream" },
        timeout: 120000,
        onUploadProgress: (p) => onProgress?.(Math.min(99, Math.round(((offset + p.loaded) / file.size) * 100)), "uploading"),
      });
    } catch (e) {
      if (e?.response?.status === 413) {
        if (chunkSize <= MIN_CHUNK) throw new Error(TOO_LARGE_MSG);
        chunkSize = Math.max(MIN_CHUNK, Math.floor(chunkSize / 2));
        continue; // retry same offset with a smaller piece
      }
      if (++attempt >= 3) throw e;
      await sleep(1500 * attempt);
      continue;
    }
    offset += chunk.size;
    attempt = 0;
    onProgress?.(Math.min(99, Math.round((offset / file.size) * 100)), "uploading");
  }
  const done = await api.post(`/uploads/${upload_id}/complete`, null, { timeout: 120000 });
  onProgress?.(99, done.data.stage || "queued");
  const result = done.data.state === "done" ? done.data.result : await waitForProcessing(upload_id, onProgress);
  onProgress?.(100, "done");
  return result;
};

export const uploadVideoInChunks = (file, onProgress) => uploadInChunks(file, "video", onProgress);
export const uploadImageInChunks = (file, onProgress) => uploadInChunks(file, "image", onProgress);
